import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { APIErrorResponseGenerator } from '@/lib/helper';

import { showToast } from '@/components/@base/toast/show-toast';
import {
  changeQuantityInCart,
  deleteFromCart,
  deleteVoucher,
  getAcademyCart,
  getCart,
  postAddToCart,
  postCheckout,
  postVoucher,
  removeFromCart,
} from '@/components/app/cart/services';
import {
  AddToCartTypes,
  CheckoutParams,
  RemoveFromCart,
} from '@/components/app/cart/services/type';

import {
  CHANGE_QUANTITY_IN_CART,
  DELETE_FROM_CART,
  DELETE_VOUCHER,
  GET_ACADEMY_CART,
  GET_CART,
  POST_CHECKOUT,
  POST_VOUCHER,
  REMOVE_FROM_CART,
} from '@/constant/query-key';
import CartModel from '@/models/cart.model';

useGetCart;

// `useGetCart` is mounted independently in many places at once (site header, and
// every section of the cart page). The academy-handoff toast/URL cleanup must only
// happen once per page load, so this guard is shared across all of those instances
// instead of living in per-hook-instance state.
let hasHandledAcademyCart = false;

export function useGetCart(options?: any): UseQueryResult<CartModel> {
  const isRenewalRef = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const [academyCartId, setAcademyCartId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has('subscription')) {
        isRenewalRef.current = true;
      }
      setAcademyCartId(searchParams.get('academyCart'));
      setIsReady(true);
    }
  }, []);

  const academyCartQuery = useQuery({
    queryKey: [GET_ACADEMY_CART, academyCartId],
    queryFn: () => getAcademyCart(academyCartId as string),
    enabled: isReady && !!academyCartId,
    retry: false,
    staleTime: Infinity,
  });

  const isAcademyCartSettled =
    !academyCartId || academyCartQuery.isSuccess || academyCartQuery.isError;

  const queryResult = useQuery<CartModel>({
    queryKey: [GET_CART],
    queryFn: () => getCart(isRenewalRef.current),
    ...options,
    enabled: options?.enabled !== false && isReady && isAcademyCartSettled,
    staleTime: isRenewalRef.current ? Infinity : 1000 * 60 * 5,
  });

  useEffect(() => {
    if (isReady && isRenewalRef.current && queryResult.data) {
      isRenewalRef.current = false;

      if (typeof window !== 'undefined') {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [queryResult.data, isReady]);

  useEffect(() => {
    if (
      academyCartId &&
      !hasHandledAcademyCart &&
      (academyCartQuery.isSuccess || academyCartQuery.isError)
    ) {
      hasHandledAcademyCart = true;

      if (academyCartQuery.isError) {
        showToast(APIErrorResponseGenerator(academyCartQuery.error));
      }

      if (typeof window !== 'undefined') {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [academyCartId, academyCartQuery.isSuccess, academyCartQuery.isError]);

  // `queryResult.isLoading` (React Query's `isPending && isFetching`) is `false` while the
  // GET_CART query is disabled — which it now is for the entire time we're waiting on `isReady`
  // and the academy cart to settle. Every cart-page component gates its skeleton on `isLoading`,
  // so without this override they'd render as if "done loading" (empty/default content) during
  // that wait instead of a skeleton. `isPending` covers the rest (no data yet, still fetching).
  const isLoading = !isReady || !isAcademyCartSettled || queryResult.isPending;

  return { ...queryResult, isLoading } as UseQueryResult<CartModel>;
}

export function usePostAddToCart() {
  return useMutation({
    mutationFn: (body: AddToCartTypes) => postAddToCart(body),
    mutationKey: [GET_CART],
  });
}

export function useDeleteFromCart() {
  return useMutation({
    mutationFn: (id: string) => deleteFromCart(id),
    mutationKey: [DELETE_FROM_CART],
  });
}

export function useRemoveFromCart() {
  return useMutation({
    mutationFn: (body: RemoveFromCart) => removeFromCart(body),
    mutationKey: [REMOVE_FROM_CART],
  });
}

export function useChangeQuantityInCart() {
  return useMutation({
    mutationFn: ({ id, quantity }: { id: string; quantity: number }) =>
      changeQuantityInCart(id, quantity),
    mutationKey: [CHANGE_QUANTITY_IN_CART],
  });
}

export function usePostVoucher() {
  return useMutation({
    mutationFn: (code: string) => postVoucher(code),
    mutationKey: [POST_VOUCHER],
  });
}

export function useDeleteVoucher() {
  return useMutation({
    mutationFn: (code: string) => deleteVoucher(code),
    mutationKey: [DELETE_VOUCHER],
  });
}

export function usePostCheckout() {
  return useMutation({
    mutationFn: (params: CheckoutParams) => postCheckout(params),
    mutationKey: [POST_CHECKOUT],
  });
}
